import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Calendar, ChevronRight, Search, Tag, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { blogService, BlogPost } from "@/lib/supabase";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { getBlogMeta } from "@/lib/utils";

const BlogSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
    {[1, 2, 3, 4, 5, 6].map((i) => (
      <div key={i} className="h-[450px] w-full rounded-2xl bg-slate-900/50 animate-pulse border border-white/5 flex flex-col">
        <div className="h-1/2 w-full bg-slate-800/50 rounded-t-2xl"></div>
        <div className="p-6 flex-1 flex flex-col gap-4">
          <div className="h-4 w-32 bg-slate-800/50 rounded"></div>
          <div className="h-6 w-full bg-slate-800/50 rounded"></div>
          <div className="h-6 w-3/4 bg-slate-800/50 rounded"></div>
          <div className="mt-auto flex gap-2">
            <div className="h-6 w-16 bg-slate-800/50 rounded-full"></div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const Blog = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const data = await blogService.getAllPosts();
        setPosts(data);
        setFilteredPosts(data);
      } catch (err) {
        console.error("Erro ao carregar o blog:", err);
        setError("Não foi possível carregar os artigos no momento. Tente novamente mais tarde.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPosts();
  }, []);

  useEffect(() => {
    const filtered = posts.filter(post => {
      const { title } = getBlogMeta(post);
      return title.toLowerCase().includes(searchTerm.toLowerCase()) || 
             post.copy_text.toLowerCase().includes(searchTerm.toLowerCase());
    });
    setFilteredPosts(filtered);
  }, [searchTerm, posts]);

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      
      {/* Hero Section Blog - Refined based on Unifer Model */}
      <section className="relative pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm z-10" />
          <img 
            src="/assets/bc-skyline.jpg" 
            alt="Nautical background" 
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Background Decor */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gold/5 rounded-full blur-[150px] -translate-y-1/2 translate-x-1/3 pointer-events-none z-10" />

        <div className="container mx-auto px-4 relative z-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto"
          >
            <span className="inline-block px-4 py-1 rounded-full bg-gold/10 text-gold text-xs font-bold uppercase tracking-[0.3em] mb-6 border border-gold/20">
              Diário de Bordo
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              O Blog da <span className="text-gold">JL Náutica</span>
            </h1>
            <p className="text-slate-400 text-lg md:text-xl leading-relaxed">
              Insights, notícias e expertise compartilhada pela equipe que entende os desafios reais da navegação.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <main className="container mx-auto px-4 lg:px-8 py-16 relative z-10">
        {/* Search Bar */}
        <div className="max-w-xl mx-auto mb-16 relative">
          <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
            <Search className="w-5 h-5 text-slate-500" />
          </div>
          <Input
            type="text"
            placeholder="Pesquisar por assunto..."
            className="pl-12 py-6 bg-slate-900/50 border-white/10 text-white rounded-full focus:ring-gold/50"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {error ? (
          <div className="flex flex-col items-center justify-center p-12 bg-white/5 rounded-2xl border border-red-500/20 text-center max-w-2xl mx-auto">
            <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
            <p className="text-red-300 text-lg">{error}</p>
          </div>
        ) : isLoading ? (
          <BlogSkeleton />
        ) : filteredPosts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, index) => {
              const { title, excerpt } = getBlogMeta(post);
              return (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Link
                    to={`/blog/${post.slug || post.id}`}
                    className="group relative block h-[480px] w-full overflow-hidden rounded-2xl bg-[#0B1121] border border-white/5 shadow-xl transition-all duration-300 hover:border-gold/50"
                  >
                    {/* Image */}
                    <div className="absolute inset-0 w-full h-1/2 overflow-hidden bg-slate-800">
                      {post.image_url ? (
                        <img
                          src={post.image_url}
                          alt={title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900 border-b border-white/5">
                          <span className="text-slate-600 text-sm font-medium tracking-widest uppercase">Postagem</span>
                        </div>
                      )}
                    </div>

                    {/* Content Container */}
                    <div className="absolute top-1/2 bottom-0 left-0 right-0 p-6 flex flex-col justify-between bg-[#0B1121] z-10 border-t border-white/5 transition-colors duration-300 group-hover:bg-[#0f172a]">
                      <div>
                        {/* Date */}
                        <div className="flex items-center gap-2 text-gold/80 mb-3">
                          <Calendar className="w-3.5 h-3.5" />
                          <span className="text-xs font-medium uppercase tracking-wider">
                            {format(new Date(post.published_at), "dd 'de' MMM, yyyy", { locale: ptBR })}
                          </span>
                        </div>

                        {/* Title */}
                        <h3 className="text-xl font-bold text-white mb-2 leading-tight line-clamp-3 group-hover:text-gold transition-colors duration-300">
                          {title}
                        </h3>

                        {/* Excerpt */}
                        <p className="text-slate-400 text-sm line-clamp-3 mt-2">
                           {excerpt}
                        </p>
                      </div>

                      {/* Footer Actions */}
                      <div className="flex items-center justify-between mt-4">
                        {post.hashtags && post.hashtags.length > 0 ? (
                           <div className="flex flex-wrap gap-2">
                              {post.hashtags.slice(0, 2).map((tag, i) => (
                                 <span key={i} className="text-[10px] text-slate-300 bg-white/5 px-2 py-1 rounded-full uppercase tracking-wider">
                                   #{tag}
                                 </span>
                              ))}
                           </div>
                        ) : <div />}

                        <div className="flex items-center text-xs font-bold text-gold opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-4 group-hover:translate-x-0">
                          LER MAIS <ChevronRight className="ml-1 w-3 h-3" />
                        </div>
                      </div>

                      {/* Animated Bottom Border */}
                      <div className="absolute bottom-0 left-0 w-full h-1 bg-gold scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />   
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20 bg-white/5 rounded-2xl border border-white/10">
            <p className="text-slate-400 text-xl">Nenhum post encontrado para sua pesquisa.</p>
            <button 
              onClick={() => setSearchTerm("")}
              className="text-gold mt-4 font-bold hover:underline"
            >
              Ver todos os posts
            </button>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default Blog;
